import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import '../design/landing.css';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import Footer from "../components/ui/footer";

export default function Landing() {
    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: true,
    };

    const [properties, setProperties] = useState([]);
    const [availableCounties, setAvailableCounties] = useState([]);
    const [filters, setFilters] = useState({ county: "", state: "", industry: "" });
    const [showDemo, setShowDemo] = useState(false);

    const handleClearFilters = () => {
        setFilters({ county: "", state: "", industry: "" });
    };

    const filteredCounties = availableCounties.filter((county) => {
        const matchesCounty = !filters.county || county.name === filters.county;
        const matchesState = !filters.state || county.state === filters.state;
        const matchesIndustry = !filters.industry || county.industry === filters.industry;
        return matchesCounty && matchesState && matchesIndustry;
    });

    useEffect(() => {
        fetch("https://api.deliquentauction.com/properties/public/")
            .then((res) => res.json())
            .then((data) => setProperties(data))
            .catch((err) => console.error("Failed to load properties:", err));
    }, []);

    useEffect(() => {
        fetch("https://api.deliquentauction.com/subscriptions/counties/") 
            .then((res) => res.json())
            .then((data) => setAvailableCounties(data))
            .catch((err) => console.error("Failed to load counties:", err));
    }, []);

    const getCountyColor = (countyName) => {
      if (!countyName) return '#999';
      let hash = 0;
      for (let i = 0; i < countyName.length; i++) {
        hash = countyName.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash % 360);
      return `hsl(${hue}, 70%, 50%)`;
    };

    const getColoredIcon = (countyName) => {
      const color = getCountyColor(countyName);
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background-color: ${color};
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid white;
        "></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
    };

    const countyMap = new Map();

    properties.forEach((prop) => {
      if (!countyMap.has(prop.county)) {
        countyMap.set(prop.county, prop);
      }
    });

    const countyMarkers = Array.from(countyMap.values());

    function FitMapToMarkers({ markers }) {
      const map = useMap();
    
      useEffect(() => {
        if (markers.length > 0) {
          const bounds = markers.map(m => [m.latitude, m.longitude]);
          map.fitBounds(bounds);
        }
      }, [markers, map]);
    
      return null;
    }

    return (
        <div className="landing">
            <header>
                <nav>
                    <img src="/logo.png" alt="Logo" />
                    <div className="link-buttons">
                      <Link to="/login" className="btn login">Login</Link>
                      <Link to="/signup" className="btn signup">Sign Up</Link>
                    </div>
                </nav>
            </header>

            <div className="landing-body">
                <div className="screenshot-car">
                    <div className="landing-titles">
                      <h3>Property Card Information</h3>
                    </div>
                    <Slider {...settings}>
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i}>
                                <img
                                    className="screenshots"
                                    src={`/properties_screen_${i}.png`}
                                    alt={`properties_screen_${i}`}
                                />
                            </div>
                        ))}
                    </Slider>
                </div>

                <div className="map">
                <div className="landing-titles">
                  <h3>Current Delinquent Property Counties</h3>
                </div>
                    {properties.length > 0 && (
                        <div className="map-container">
                            <MapContainer
                                center={[properties[0].latitude, properties[0].longitude]}
                                zoom={6}
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <FitMapToMarkers markers={countyMarkers} />
                                {countyMarkers.map((property, idx) => (
                                  <Marker
                                    key={property.id || idx}
                                    position={[property.latitude, property.longitude]}
                                    icon={getColoredIcon(property.county)}
                                  >
                                    <Popup>
                                      {property.county}<br />
                                      {property.state}<br />
                                      {property.address}
                                    </Popup>
                                  </Marker>
                                ))}
                            </MapContainer>
                        </div>
                    )}
                </div>

                <div className="county-pricing">
                <div className="landing-titles">
                  <h3>Monthly Pricing Model</h3>
                </div>
                    <div className="county-filters">
                        <select
                            value={filters.county}
                            onChange={(e) => setFilters({ ...filters, county: e.target.value })}
                        >
                            <option value="">All Counties</option>
                            {[...new Set(filteredCounties.map((c) => c.name))].sort().map((name) => (
                                <option key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={filters.state}
                            onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                        >
                            <option value="">All States</option>
                            {[...new Set(filteredCounties.map((c) => c.state))].sort().map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                        </select>

                        <select
                            value={filters.industry}
                            onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
                        >
                            <option value="">All Industries</option>
                            {[...new Set(filteredCounties.map((c) => c.industry))].sort().map((industry) => (
                                <option key={industry} value={industry}>
                                    {industry}
                                </option>
                            ))}
                        </select>

                        <button onClick={handleClearFilters} className="clear-filters-button">
                            Clear Filters
                        </button>
                    </div>

                    <div className="county-list-body">
                        {filteredCounties.length === 0 ? (
                            <p>No available counties to subscribe to.</p>
                        ) : (
                            [...filteredCounties]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((county) => (
                                    <div key={county.id} className="county-card">
                                        <div className="county-card-title">County</div>
                                        <div className="county-card-title">State</div>
                                        <div className="county-card-title">Distress List</div>
                                        <div className="county-card-title">Leads</div>
                                        <div className="county-card-title">Monthly Cost</div>

                                        <div className="county-card-value">{county.name}</div>
                                        <div className="county-card-value">{county.state}</div>
                                        <div className="county-card-value">{county.industry}</div>
                                        <div className="county-card-value">{county.lead_count}</div>
                                        <div className="county-card-value">
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            }).format(county.price)}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                </div>

                <div className="about-us">
                    <div className="landing-titles">
                      <h3>About Us</h3>
                    </div>
                    <div className="demo-wrapper">
                      <button className="demo-button" onClick={() => setShowDemo(true)}>
                        View Demo Video
                      </button>
                    </div>
                    {showDemo && (
                      <div className="modal-overlay" onClick={() => setShowDemo(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                          <div className="demo-text">
                            <h3>Demo</h3>
                            <button className="demo-close-button" onClick={() => setShowDemo(false)}>Close</button>
                          </div>
                          <video controls autoPlay style={{ width: '100%' }}>
                            <source src="/deliquentauction_demo.mov" type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </div>
                    )}
                    <div className="about-us-note">
                    <section>
                      <p>
                        <strong>Deliquent Auction</strong> helps investors and professionals find and manage tax deed, foreclosure, and sheriff sale leads — all in one place.
                      </p>
                      <p>
                        We built this to fix scattered, outdated, overpriced data with a simple, centralized tool.
                      </p>
                      <p>
                        <strong>Note:</strong> All data is public. We don't claim ownership. Listings expire 7 days after sale. Accuracy is high, but not guaranteed. Always verify property info.
                      </p>
                      <p>
                        For inquiries please send us an email at <strong>support@deliquentauction.com</strong>
                      </p>
                    </section>
                    </div>
                </div>
            </div>
          <Footer />
        </div>
    );
}